import imageCompression from "browser-image-compression";
import { mediaApi, uploadFileWithTicket } from "@bw/shared/api";
import type { PhotoUploadRequest } from "@bw/shared/types/generated";

/** At most four photos may ride a single post (matches the backend cap). */
export const MAX_MEDIA_PER_POST = 4;

/** Client-compression options: downscale + cap the byte size before upload. */
const COMPRESSION_OPTIONS = {
  maxWidthOrHeight: 1600,
  maxSizeMB: 0.5,
  useWebWorker: true,
} as const;

/** The content types the backend accepts (PhotoUploadRequest.content_type). */
type AllowedContentType = PhotoUploadRequest["content_type"];
const ALLOWED_CONTENT_TYPES: readonly AllowedContentType[] = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

function isAllowedContentType(type: string): type is AllowedContentType {
  return (ALLOWED_CONTENT_TYPES as readonly string[]).includes(type);
}

/** The result of a successful device-photo upload. Only `publicUrl` is persisted
 * into a post's media_urls (never the upload_url). */
export interface UploadedPostPhoto {
  /** The PERMANENT url that goes into the post's media_urls. */
  publicUrl: string;
  /** The server object key (data only — callers must NOT use it as a React key). */
  objectKey: string;
}

/**
 * The ONE device-photo upload path for player post composers/editors (extracted
 * from PostComposer so EditPostDialog reuses it rather than copy-pasting — one
 * authoritative flow, no dual-path):
 *
 *   1. compress (downscale + size-cap) via browser-image-compression;
 *   2. reject anything outside the backend's JPEG/PNG/WebP allowlist client-side
 *      BEFORE the ticket request (e.g. a HEIC the compressor passed through),
 *      surfacing the same honest failure as a network error;
 *   3. request a presigned POST ticket through the shared client;
 *   4. pass the compressed file + ticket through the shared media transport:
 *      anonymous multipart for configured object storage, authenticated raw
 *      bytes for the same-origin preview fallback;
 *   5. resolve a preview-relative `public_url` against the current origin and
 *      return the permanent absolute URL (+ object_key).
 *
 * Throws on ANY failure — the caller surfaces it honestly (role="alert") and
 * adds NO photo. The caller also owns the in-flight `uploading` state that gates
 * submit (never publish/save mid-upload — that would silently drop the photo).
 */
export async function uploadPostPhoto(file: File): Promise<UploadedPostPhoto> {
  const compressed = await imageCompression(file, COMPRESSION_OPTIONS);

  if (!isAllowedContentType(compressed.type)) {
    throw new Error(`Unsupported image type: ${compressed.type}`);
  }

  const ticket = await mediaApi.requestPhotoUpload({
    content_type: compressed.type,
    content_length: compressed.size,
  });

  const uploaded = await uploadFileWithTicket(compressed, ticket);

  return {
    publicUrl: uploaded.publicUrl,
    objectKey: uploaded.objectKey,
  };
}
